import React, { useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import Lightbox from 'yet-another-react-lightbox'
import Captions from 'yet-another-react-lightbox/plugins/captions'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import Download from 'yet-another-react-lightbox/plugins/download'
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/captions.css'
import 'yet-another-react-lightbox/plugins/thumbnails.css'
import { getDateOnly } from '../helpers/formatDate'
import { Table } from 'react-bootstrap'
import ClientHeader from '../components/ClientHeader/ClientHeader'
import DetailStatusMobileHeader from '../components/DetailStatusMobileHeader'
import DetailsSummarySection from '../components/details/summary/DetailsSummarySection'
import TotalsSummaryCard from '../components/TotalsSummaryCard'
import MobileEmptyStateCard from '../components/MobileEmptyStateCard'
import { MobileExpandableDetailCard } from '../components/mobile'
import GoogleIcon from '../components/icon/GoogleIcon'
import { formatMoney, parseDocumentType } from '../helpers/utils'
import { layoutMobileMediaQuery } from '../layout/layoutConstants'
import { CHARGE_HEADER_CONFIG } from './chargeConstants'
import {
  buildChargeExtraFields,
  buildChargePaymentAttachmentCounters,
  buildChargeSummaryFields,
  buildChargeTotalRows
} from './chargeDetailsHelpers'

const getFormattedDateOnly = (date) => {
  if (!date) return 'N/A'
  return getDateOnly(date) || 'N/A'
}

const renderDocumentReference = (model) => {
  if (model.document_type === 'DR' && model.document_reference) {
    return (
      <a
        className="ubiqua-link"
        target="_blank"
        rel="noopener noreferrer"
        href={`/entregas/${model.document_reference}`}
      >
        {model.document_reference}
      </a>
    )
  }

  return model.document_reference || 'N/A'
}

const attachmentButtonSx = {
  p: 0,
  border: 0,
  background: 'transparent',
  lineHeight: 0,
  cursor: 'pointer'
}

function AttachmentBadge({ count = 0, children }) {
  if (!count) return '-'

  return (
    <Badge
      badgeContent={count}
      sx={{
        '& .MuiBadge-badge': {
          backgroundColor: '#FF8A3D',
          color: '#FFFFFF',
          fontWeight: 700,
          minWidth: 22,
          height: 22,
          borderRadius: 11
        }
      }}
    >
      {children}
    </Badge>
  )
}

function ImageAttachmentCell({ count = 0, onClick }) {
  return (
    <AttachmentBadge count={count}>
      <Box
        component="button"
        type="button"
        aria-label={`Ver ${count} ${count === 1 ? 'imagen' : 'imágenes'} del pago`}
        onClick={onClick}
        sx={attachmentButtonSx}
      >
        <GoogleIcon name="image" size={22} color="#50739A" />
      </Box>
    </AttachmentBadge>
  )
}

function FileAttachmentCell({ count = 0, onClick }) {
  return (
    <AttachmentBadge count={count}>
      <Box
        component="button"
        type="button"
        aria-label={`Abrir ${count} adjunto${count === 1 ? '' : 's'} del pago`}
        onClick={onClick}
        sx={attachmentButtonSx}
      >
        <GoogleIcon name="description" size={22} color="#50739A" />
      </Box>
    </AttachmentBadge>
  )
}

function getAttachmentFileName(fileUrl = '', index = 0) {
  try {
    const parsedUrl = new URL(fileUrl)
    const decodedPath = decodeURIComponent(parsedUrl.pathname)
    const fileName = decodedPath.split('/').pop()

    return fileName || `Documento ${index + 1}`
  } catch (error) {
    return `Documento ${index + 1}`
  }
}

function FileAttachmentsDialog({ open, files = [], onClose }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800, color: '#0A2540' }}>
        Adjuntos del pago
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.25}>
          {files.map((fileUrl, index) => (
            <Box
              key={`${fileUrl}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                border: '1px solid #E5E7EB',
                borderRadius: 1,
                px: 1.5,
                py: 1.25
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                <GoogleIcon name="description" size={22} color="#50739A" />
                <Typography
                  title={getAttachmentFileName(fileUrl, index)}
                  sx={{
                    color: '#0A2540',
                    fontSize: 14,
                    fontWeight: 700,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {getAttachmentFileName(fileUrl, index)}
                </Typography>
              </Stack>
              <Button
                component="a"
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="text"
                size="small"
                sx={{ flexShrink: 0, fontWeight: 800, textTransform: 'none' }}
              >
                Abrir
              </Button>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ fontWeight: 800, textTransform: 'none' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const chargeCardDividerColor = 'rgba(32, 49, 69, 0.18)'

function PaymentMethodMobileCards({
  paymentMethods = [],
  attachmentCounters = [],
  onOpenImages,
  onOpenFiles
}) {
  const [expandedCards, setExpandedCards] = useState({})

  useEffect(() => {
    setExpandedCards(
      paymentMethods.reduce((acc, model, index) => {
        acc[`${model.payment_type || 'payment'}-${index}`] = index === 0
        return acc
      }, {})
    )
  }, [paymentMethods])

  if (!paymentMethods.length) {
    return (
      <MobileEmptyStateCard
        title="No hay métodos de pago"
        description="Cuando este cobro tenga métodos de pago registrados, aparecerán aquí."
      />
    )
  }

  return (
    <div className="mb-10">
      {paymentMethods.map((model, index) => {
        const cardKey = `${model.payment_type || 'payment'}-${index}`
        const attachmentData = attachmentCounters[index] || {}

        return (
          <div key={cardKey} className="mb-10">
            <MobileExpandableDetailCard
              code={model.payment_type || 'Método de pago'}
              expanded={Boolean(expandedCards[cardKey])}
              onToggleExpand={() =>
                setExpandedCards((current) => ({
                  ...current,
                  [cardKey]: !current[cardKey]
                }))
              }
              expandAriaLabel={
                expandedCards[cardKey]
                  ? 'Ocultar detalle del método de pago'
                  : 'Mostrar detalle del método de pago'
              }
              detailRegionAriaLabel={`Detalle del método de pago ${model.payment_type || index + 1}`}
              summaryRows={[
                {
                  label: 'Monto',
                  value: formatMoney(model.amount),
                  alignLabelAndValue: true
                },
                {
                  label: 'Fecha',
                  value: getFormattedDateOnly(model.date),
                  alignLabelAndValue: true
                }
              ]}
              detailRows={[
                {
                  label: 'Banco',
                  value: model.bank_name || 'N/A',
                  alignLabelAndValue: true,
                  dividerColor: chargeCardDividerColor
                },
                {
                  label: 'Número de referencia',
                  value: model.reference || 'N/A',
                  alignLabelAndValue: true,
                  dividerColor: chargeCardDividerColor
                },
                {
                  label: 'Imagen',
                  value: (
                    <Box sx={{ pt: 0.25 }}>
                      <ImageAttachmentCell
                        count={attachmentData.imagesCount || 0}
                        onClick={() => onOpenImages(attachmentData.imageUrls || [])}
                      />
                    </Box>
                  ),
                  alignLabelAndValue: true,
                  dividerColor: chargeCardDividerColor
                },
                {
                  label: 'Adjuntos',
                  value: (
                    <Box sx={{ pt: 0.25 }}>
                      <FileAttachmentCell
                        count={attachmentData.filesCount || 0}
                        onClick={() => onOpenFiles(attachmentData.fileUrls || [])}
                      />
                    </Box>
                  ),
                  alignLabelAndValue: true,
                  dividerColor: chargeCardDividerColor
                }
              ]}
            />
          </div>
        )
      })}
    </div>
  )
}

function ChargeDocumentsMobileCards({ details = [] }) {
  const [expandedCards, setExpandedCards] = useState({})

  useEffect(() => {
    setExpandedCards(
      details.reduce((acc, model, index) => {
        acc[`${model.document_reference || 'document'}-${index}`] = index === 0
        return acc
      }, {})
    )
  }, [details])

  if (!details.length) {
    return (
      <MobileEmptyStateCard
        title="No hay documentos"
        description="Cuando este cobro tenga documentos aplicados, aparecerán aquí."
      />
    )
  }

  return (
    <div className="mb-10">
      {details.map((model, index) => {
        const cardKey = `${model.document_reference || 'document'}-${index}`
        const dueDate =
          model.document_type === 'DR' || model.document_type === 'P'
            ? 'N/A'
            : getDateOnly(model.document_due_date) || 'N/A'

        return (
          <div key={cardKey} className="mb-10">
            <MobileExpandableDetailCard
              code={model.document_reference || 'Documento'}
              expanded={Boolean(expandedCards[cardKey])}
              onToggleExpand={() =>
                setExpandedCards((current) => ({
                  ...current,
                  [cardKey]: !current[cardKey]
                }))
              }
              expandAriaLabel={
                expandedCards[cardKey]
                  ? 'Ocultar detalle del documento'
                  : 'Mostrar detalle del documento'
              }
              detailRegionAriaLabel={`Detalle del documento ${model.document_reference || index + 1}`}
              summaryRows={[
                {
                  label: 'Tipo',
                  value: parseDocumentType(model.document_type),
                  alignLabelAndValue: true
                },
                {
                  label: 'Pagado',
                  value: formatMoney(model.document_amount),
                  alignLabelAndValue: true
                }
              ]}
              detailRows={[
                {
                  label: 'Fecha',
                  value: getFormattedDateOnly(model.document_date),
                  alignLabelAndValue: true,
                  dividerColor: chargeCardDividerColor
                },
                {
                  label: 'Fecha de vencimiento',
                  value: dueDate,
                  alignLabelAndValue: true,
                  dividerColor: chargeCardDividerColor
                },
                {
                  label: 'Saldo',
                  value: model.document_total
                    ? formatMoney(model.document_total)
                    : formatMoney(model.pending_balance),
                  alignLabelAndValue: true,
                  dividerColor: chargeCardDividerColor
                },
                {
                  label: 'Descuento',
                  value: formatMoney(model.early_payment_discount_amount),
                  alignLabelAndValue: true,
                  dividerColor: chargeCardDividerColor
                }
              ]}
            />
          </div>
        )
      })}
    </div>
  )
}

export default function ChargeDetailsTab({
  activity: initialActivity = {},
  transactionUuid,
  statusUI,
  showStatusInHeader = false
}) {
  const [activity, setActivity] = useState(initialActivity || {})
  const [imageGallery, setImageGallery] = useState([])
  const [showImageGallery, setShowImageGallery] = useState(false)
  const [fileAttachments, setFileAttachments] = useState([])
  const [showFileAttachments, setShowFileAttachments] = useState(false)
  const isMobileLayout = useMediaQuery(layoutMobileMediaQuery)

  useEffect(() => {
    setActivity(initialActivity || {})
  }, [initialActivity])

  const customFields = Array.isArray(activity?.custom_fields) ? activity.custom_fields : []
  const paymentMethods = Array.isArray(activity?.payment_methods)
    ? activity.payment_methods.filter(model => Number(model?.amount) > 0)
    : []
  const summaryFields = useMemo(() => buildChargeSummaryFields(activity), [activity])
  const extraFields = useMemo(() => buildChargeExtraFields(customFields), [customFields])
  const totalRows = useMemo(() => buildChargeTotalRows(activity), [activity])
  const attachmentCounters = useMemo(
    () => buildChargePaymentAttachmentCounters(paymentMethods),
    [paymentMethods]
  )
  const details = Array.isArray(activity?.details) ? activity.details : []

  const detailsTitle = transactionUuid ? 'Detalles de cobro:' : 'Detalles:'

  const handleOpenImageGallery = (images = []) => {
    if (!images.length) return

    setImageGallery(images)
    setShowImageGallery(true)
  }

  const handleOpenFileAttachments = (files = []) => {
    const validFiles = files.filter(Boolean)

    if (!validFiles.length) return

    if (validFiles.length === 1) {
      window.open(validFiles[0], '_blank', 'noopener,noreferrer')
      return
    }

    setFileAttachments(validFiles)
    setShowFileAttachments(true)
  }

  return (
    <div className="container-fluid px-0">
      {showStatusInHeader ? (
        <DetailStatusMobileHeader
          statusUI={statusUI}
          fallbackStatus={activity?.status}
          title={detailsTitle}
        />
      ) : (
        <div>
          <div className="show-details-header">
            <h3 className="margin-text-subtitle-2">{detailsTitle}</h3>
          </div>
          <hr />
        </div>
      )}

      <ClientHeader data={activity} config={CHARGE_HEADER_CONFIG} />

      <DetailsSummarySection
        summaryFields={summaryFields}
        extraFields={extraFields}
        summarySingleItemFullWidth
        summaryTitle="DETALLES DEL COBRO"
        summaryOpenLabel="Abrir detalles del cobro"
        summaryCloseLabel="Cerrar detalles del cobro"
      />

      <div className="show-data-details">
        <div>
          <b>Métodos de pago:</b>
        </div>
      </div>

      {isMobileLayout ? (
        <PaymentMethodMobileCards
          paymentMethods={paymentMethods}
          attachmentCounters={attachmentCounters}
          onOpenImages={handleOpenImageGallery}
          onOpenFiles={handleOpenFileAttachments}
        />
      ) : (
        <Table hover responsive>
          <thead className="table-header-gray-mui">
            <tr>
              <th>TIPO DE PAGO</th>
              <th>FECHA</th>
              <th>BANCO</th>
              <th>NÚMERO DE REFERENCIA</th>
              <th>MONTO</th>
              <th className="text-center">IMAGEN</th>
              <th className="text-center">ADJUNTOS</th>
            </tr>
          </thead>
          <tbody>
            {paymentMethods.length ? (
              paymentMethods.map((model, index) => (
                <tr key={`${model.payment_type || 'payment'}-${index}`}>
                  <td>{model.payment_type || 'N/A'}</td>
                  <td>{getFormattedDateOnly(model.date)}</td>
                  <td>{model.bank_name || 'N/A'}</td>
                  <td>{model.reference || 'N/A'}</td>
                  <td>{formatMoney(model.amount)}</td>
                  <td className="text-center">
                    <ImageAttachmentCell
                      count={attachmentCounters[index]?.imagesCount || 0}
                      onClick={() => handleOpenImageGallery(attachmentCounters[index]?.imageUrls || [])}
                    />
                  </td>
                  <td className="text-center">
                    <FileAttachmentCell
                      count={attachmentCounters[index]?.filesCount || 0}
                      onClick={() => handleOpenFileAttachments(attachmentCounters[index]?.fileUrls || [])}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7}>No hay métodos de pago registrados</td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <TotalsSummaryCard rows={[totalRows[0]]} noWrapLabels compact maxWidth={460} />

      <div className="show-data-details">
        <div>
          <b>Documentos:</b>
        </div>
      </div>

      {isMobileLayout ? (
        <ChargeDocumentsMobileCards details={details} />
      ) : (
        <Table hover responsive>
          <thead className="table-header-gray-mui">
            <tr>
              <th>NÚMERO DE DOCUMENTO</th>
              <th>FECHA</th>
              <th>FECHA DE VENCIMIENTO</th>
              <th>TIPO</th>
              <th>SALDO</th>
              <th>DESCUENTO</th>
              <th>PAGADO</th>
            </tr>
          </thead>
          <tbody>
            {details.length ? (
              details.map((model, index) => (
                <tr key={`${model.document_reference || 'document'}-${index}`}>
                  <td className="td-text-limited">{renderDocumentReference(model)}</td>
                  <td>{getFormattedDateOnly(model.document_date)}</td>
                  <td>
                    {model.document_type === 'DR' || model.document_type === 'P'
                      ? 'N/A'
                      : getDateOnly(model.document_due_date) || 'N/A'}
                  </td>
                  <td>{parseDocumentType(model.document_type)}</td>
                  <td>
                    {model.document_total
                      ? formatMoney(model.document_total)
                      : formatMoney(model.pending_balance)}
                  </td>
                  <td>{formatMoney(model.early_payment_discount_amount)}</td>
                  <td>{formatMoney(model.document_amount)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7}>No hay documentos aplicados</td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <TotalsSummaryCard rows={[totalRows[1]]} noWrapLabels compact maxWidth={460} />
      <FileAttachmentsDialog
        open={showFileAttachments}
        files={fileAttachments}
        onClose={() => setShowFileAttachments(false)}
      />
      <Lightbox
        plugins={[Captions, Zoom, Download, Thumbnails]}
        styles={{
          container: { backgroundColor: 'rgba(0, 0, 0, .8)' },
          captionsDescriptionContainer: {
            display: 'flex',
            justifyContent: 'center'
          }
        }}
        open={showImageGallery}
        close={() => setShowImageGallery(false)}
        animation={{ swipe: 0 }}
        carousel={{ finite: true }}
        zoom={{ scrollToZoom: true }}
        slides={imageGallery.map((link, index) => ({
          src: link,
          description: `${index + 1}/${imageGallery.length}`,
          download: { url: link, filename: `pago-imagen-${index + 1}` }
        }))}
      />
    </div>
  )
}
